import React, { useMemo, useState } from "react";
import { Code2, Copy, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useCategories } from "../context/CategoriesContext";

const ALL_VALUE = "__all__";

export function CarrdEmbedDialog() {
  const { categories } = useCategories();
  const [open, setOpen] = useState(false);
  const [height, setHeight] = useState(1100);
  const [categoryValue, setCategoryValue] = useState(ALL_VALUE); // category id or ALL_VALUE
  const [copied, setCopied] = useState(false);

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL ||
    (typeof window !== "undefined" ? window.location.origin : "");

  const selectedCategory = useMemo(() => {
    if (categoryValue === ALL_VALUE) return null;
    return categories.find((c) => c.id === categoryValue) || null;
  }, [categoryValue, categories]);

  const embedUrl = useMemo(() => {
    const base = `${backendUrl.replace(/\/$/, "")}/?embed=1`;
    return selectedCategory ? `${base}&category=${selectedCategory.slug}` : base;
  }, [backendUrl, selectedCategory]);

  const titleAttr = selectedCategory
    ? `${selectedCategory.name} PDFs`
    : "PDF Library";

  const snippet = useMemo(
    () =>
      `<iframe src="${embedUrl}" width="100%" height="${height}" style="border:0;display:block;width:100%;" loading="lazy" allow="autoplay" title="${titleAttr}"></iframe>`,
    [embedUrl, height, titleAttr]
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success("Embed snippet copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Unable to copy");
    }
  };

  const headingTitle = selectedCategory
    ? `Embed ${selectedCategory.name} on Carrd`
    : "Embed the full library on Carrd";
  const headingDesc = selectedCategory
    ? `Drops only the ${selectedCategory.name} PDFs into a Carrd Embed (Type: Code). Use this on the ${selectedCategory.name}-specific Carrd page.`
    : "Paste this snippet into a Carrd Embed element (Type: Code). Your whole library will show inside the page.";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="h-11 border-slate-300 rounded-lg"
          data-testid="embed-carrd-btn"
        >
          <Code2 className="h-4 w-4" />
          Embed on Carrd
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl" data-testid="embed-carrd-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold">{headingTitle}</DialogTitle>
          <DialogDescription>{headingDesc}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Scope picker */}
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
              What to embed
            </Label>
            <Select value={categoryValue} onValueChange={setCategoryValue}>
              <SelectTrigger data-testid="embed-carrd-scope">
                <SelectValue placeholder="Pick a scope" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE} data-testid="embed-carrd-scope-all">
                  Full library (all categories)
                </SelectItem>
                {categories.map((c) => (
                  <SelectItem
                    key={c.id}
                    value={c.id}
                    data-testid={`embed-carrd-scope-${c.slug}`}
                  >
                    Only {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 leading-relaxed">
              Pick a specific sport to embed it on its own Carrd page. Visitors will only
              see PDFs in that category.
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Snippet</span>
              <Button size="sm" variant="outline" onClick={copy} className="h-8" data-testid="embed-carrd-copy">
                {copied ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copied" : "Copy snippet"}
              </Button>
            </div>
            <code
              className="block text-xs break-all text-slate-800 font-mono whitespace-pre-wrap leading-relaxed"
              data-testid="embed-carrd-snippet"
            >
              {snippet}
            </code>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 items-end">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Height (px)</Label>
              <Input
                type="number"
                min={400}
                max={4000}
                step={50}
                value={height}
                onChange={(e) => setHeight(Number(e.target.value) || 1100)}
                className="h-10"
                data-testid="embed-carrd-height"
              />
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              Tip: Carrd embeds don't auto-resize. Pick a height tall enough for your full list, or let visitors
              scroll inside the frame. Start around <span className="font-semibold">1100px</span>.
            </p>
          </div>

          <div className="rounded-xl border border-brand-accent bg-brand-accent-soft p-4 text-sm text-slate-700 leading-relaxed">
            <div className="flex items-center gap-2 mb-2 text-brand-primary font-bold text-xs uppercase tracking-[0.15em]">
              <ExternalLink className="h-3.5 w-3.5" />
              Preview URL
            </div>
            <a
              href={embedUrl}
              target="_blank"
              rel="noreferrer"
              className="text-brand-primary hover:text-brand-primary font-mono text-xs break-all underline underline-offset-2"
              data-testid="embed-carrd-preview-link"
            >
              {embedUrl}
            </a>
            <p className="text-xs text-slate-500 mt-2">
              Open in a new tab to see exactly what your visitors will see inside the Carrd embed.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
